import axios from "axios";
import { putCache, getCache, enqueueWrite } from "@/lib/offlineDb";
import { autoNotify } from "@/lib/feedback";
import { currentActor, signedOut, setSessionActor } from "@/lib/sessionPrivacy";
import { securityHeaders } from "@/lib/securityHeaders";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Auth token lives in an httpOnly cookie set by the backend (XSS-safe).
// 30s timeout guards against dead / hung backends locking the UI. Long-running
// upload/AI endpoints override this per-call.
export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    timeout: 30000,
});

// ---------------- Offline middleware ----------------
//
// GET requests against these path prefixes are cached in IndexedDB
// (response.data only). When the network is unreachable we hydrate from cache.
const CACHEABLE_GET_PREFIXES = [
    "/tree",
    "/family/",
    "/memories",
    "/culture/",
];

// Write requests (POST/PUT/PATCH/DELETE) against these prefixes are queued
// when the user is offline, so they sync once the connection is restored.
const QUEUEABLE_WRITE_PREFIXES = [
    "/tree/persons",
    "/tree/relationships",
    "/tree/persons/", // edit/delete by id
    "/culture/",
    "/legal/",
];

function startsWithAny(url, prefixes) {
    if (!url) return false;
    // Strip query string for prefix match
    const path = url.split("?")[0];
    return prefixes.some((p) => path === p || path.startsWith(p));
}

function isNetworkError(err) {
    // axios: no err.response when the request never hit the server (offline/DNS/CORS)
    if (err?.response) return false;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
    return err?.code === "ERR_NETWORK" || err?.message === "Network Error";
}

function isOnline() {
    return typeof navigator === "undefined" ? true : navigator.onLine;
}

// Skip caching/queueing entirely when the caller opts out (e.g. login flow).
function offlineSkipped(config) {
    return config?.skipOffline === true;
}

api.interceptors.request.use(async config => {
    config.__actor = currentActor();
    if (config.__actor) config.headers['X-Session-Actor'] = config.__actor;
    if (!['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) {
        if (signedOut() && !config.url?.startsWith('/auth/')) throw new Error("Please sign in again.");
        Object.assign(config.headers, await securityHeaders(false));
        if (config.__actor && config.__actor !== currentActor()) throw new Error("Account changed; please retry.");
        if (config.__actor) config.headers['X-Session-Actor'] = config.__actor;
    }
    return config;
});

api.interceptors.response.use(
    async (res) => {
        const cfg = res.config || {};
        try {
            autoNotify(cfg, res.status);
        } catch (e) {
            console.debug("[api] autoNotify failed:", e?.message || e);
        }
        try {
            if (
                !offlineSkipped(cfg) &&
                cfg.__actor && cfg.__actor === currentActor() &&
                (cfg.method || "get").toLowerCase() === "get" &&
                startsWithAny(cfg.url, CACHEABLE_GET_PREFIXES) &&
                res.status >= 200 &&
                res.status < 300
            ) {
                await putCache("GET", cfg.url, res.data);
            }
        } catch (e) {
            console.debug("[api] cache write failed:", e?.message || e);
        }
        return res;
    },
    async (err) => {
        const cfg = err?.config || {};
        const method = (cfg.method || "get").toLowerCase();
        if (err?.response?.status === 409 && err?.response?.data?.detail?.code === "SESSION_CHANGED") {
            setSessionActor(null);
            window.location.assign("/login");
            return Promise.reject(err);
        }

        // Auth redirect (preserve existing behavior)
        if (err?.response?.status === 401 && !cfg.skipAuthRedirect) {
            setSessionActor(null);
            if (typeof window !== "undefined" && window.location.pathname !== "/login") {
                window.location.assign("/login");
            }
            return Promise.reject(err);
        }

        // Plan limit / feature gate — surface a global upsell modal.
        if (err?.response?.status === 402) {
            const detail = err.response?.data?.detail;
            if (detail && typeof detail === "object" &&
                (detail.code === "plan_limit_reached" || detail.code === "plan_feature_required")) {
                if (typeof window !== "undefined") {
                    try {
                        window.dispatchEvent(new CustomEvent("vt:plan-limit", { detail }));
                    } catch (e) {
                        console.debug("[api] dispatchEvent failed:", e?.message || e);
                    }
                }
            }
        }

        const networkDown = isNetworkError(err);

        // GET fallback to cache when offline
        if (
            networkDown &&
            !offlineSkipped(cfg) &&
            cfg.__actor && cfg.__actor === currentActor() &&
            method === "get" &&
            startsWithAny(cfg.url, CACHEABLE_GET_PREFIXES)
        ) {
            const row = await getCache("GET", cfg.url);
            if (row && row.data !== undefined) {
                return {
                    data: row.data,
                    status: 200,
                    statusText: "OK (offline cache)",
                    headers: {},
                    config: cfg,
                    request: null,
                    fromCache: true,
                    cachedAt: row.ts,
                };
            }
        }

        // Queue writes when offline
        if (
            networkDown &&
            !offlineSkipped(cfg) &&
            ["post", "put", "patch", "delete"].includes(method) &&
            cfg.__actor && cfg.__actor === currentActor() &&
            startsWithAny(cfg.url, QUEUEABLE_WRITE_PREFIXES)
        ) {
            await enqueueWrite({
                method: method.toUpperCase(),
                url: cfg.url,
                data: cfg.data ? safeParse(cfg.data) : undefined,
            });
            if (typeof window !== "undefined") {
                try {
                    window.dispatchEvent(new CustomEvent("vt:offline-queued"));
                } catch (e) {
                    console.debug("[api] offline event dispatch failed:", e?.message || e);
                }
            }
            return {
                data: { queued: true, offline: true, url: cfg.url },
                status: 202,
                statusText: "Accepted (queued offline)",
                headers: {},
                config: cfg,
                request: null,
                queued: true,
            };
        }

        return Promise.reject(err);
    },
);

function safeParse(body) {
    if (body == null) return undefined;
    if (typeof body !== "string") return body;
    try {
        return JSON.parse(body);
    } catch (e) {
        // body wasn't JSON — return as-is. Caller can decide.
        return body;
    }
}

// Best-effort: also surface a queued event when going offline mid-flight for
// requests we *can't* see (e.g. fetch() calls outside axios). Pages can listen
// to "vt:offline-sync" to refetch after the queue drains.
if (typeof window !== "undefined" && isOnline === undefined) {
    /* no-op placeholder so the import has a side effect */
}

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
